/**
 * Role-specific resume guides. One data file, one route template.
 *
 * These exist to rank for "<role> resume" and to be quotable by an answer
 * engine, so every field carries something a generic resume article does not:
 * the literal parent terms an applicant tracking system fails to infer, and
 * before/after bullets that show the rewrite rather than describe it.
 *
 * Nothing here is auto-generated. A thin page per keyword is a doorway page;
 * adding a role means writing the role.
 */

export type RoleGuide = {
  slug: string;
  name: string;
  /** ISO date. Freshness is both a ranking signal and an AI-citation signal. */
  updated: string;
  metaTitle: string;
  metaDescription: string;
  /** The 40-60 word, self-contained answer. Rendered first on the page. */
  definition: string;
  /** Body paragraphs under "How an ATS reads this resume". 2-4 sentences each. */
  atsNotes: string[];
  /** Literal terms a scanner matches. Ordered by how often postings ask for them. */
  hardKeywords: string[];
  softKeywords: string[];
  /** The scanner matches letters. It never infers the parent from the child. */
  parentTerms: Array<{ child: string; parent: string }>;
  sectionOrder: string[];
  sectionRationale: string;
  bullets: Array<{ before: string; after: string; why: string }>;
  mistakes: Array<{ title: string; body: string }>;
  faqs: Array<{ q: string; a: string }>;
  /** Seeds the /jobs link at the end of the page. */
  jobsQuery: string;
};

export const ROLE_GUIDES: RoleGuide[] = [
  {
    slug: "software-engineer",
    name: "Software Engineer",
    updated: "2026-08-29",
    metaTitle: "Software Engineer Resume: ATS Keywords & Examples",
    metaDescription:
      "What an applicant tracking system actually matches on a software engineer resume: the literal keyword list, the parent terms scanners never infer, and before/after bullets under a fixed bullet budget.",
    definition:
      "A software engineer resume passes an applicant tracking system when its work bullets contain the job description's literal technology names, not synonyms or parent concepts. The scanner matches strings. It will not read PostgreSQL as SQL, React as JavaScript, or Kubernetes as container orchestration unless both terms appear.",
    atsNotes: [
      "Most engineering postings are screened by keyword overlap before a person opens the file. Overlap is counted against the whole document, but a recruiter only credits a term that sits inside a work or project bullet with context: what you used, how you used it, why it mattered, and where.",
      "A Skills list still earns parser credit and is the correct home for technology this particular job never asked for. It earns no human credit, which is why moving a term from Skills into a bullet is usually worth more than adding a new term.",
      "Seniority is inferred from scope, not from the word senior. Team size, system ownership, on-call responsibility, and the blast radius of what you shipped move a resume up a level far more reliably than a title.",
    ],
    hardKeywords: [
      "Python", "Java", "JavaScript", "TypeScript", "Go", "SQL", "REST API", "GraphQL",
      "Git", "Docker", "Kubernetes", "AWS", "CI/CD", "unit testing", "code review",
      "microservices", "PostgreSQL", "Redis", "Linux", "Agile", "system design",
    ],
    softKeywords: [
      "cross-functional collaboration", "technical documentation", "mentoring",
      "incident response", "stakeholder communication", "on-call ownership",
    ],
    parentTerms: [
      { child: "PostgreSQL", parent: "SQL" },
      { child: "React", parent: "JavaScript" },
      { child: "FastAPI", parent: "Python" },
      { child: "Kubernetes", parent: "container orchestration" },
      { child: "GitHub Actions", parent: "CI/CD" },
      { child: "Terraform", parent: "infrastructure as code" },
    ],
    sectionOrder: ["Contact", "Summary", "Experience", "Projects", "Education", "Skills"],
    sectionRationale:
      "Experience leads because work history is what the scanner and the recruiter both weight most. Skills sits at the end — it is the parking lot for secondary keywords and anything the bullets could not carry in context.",
    bullets: [
      {
        before: "Worked on the backend API for the payments team.",
        after:
          "Built and owned 14 REST endpoints for the payments service in Python and FastAPI on PostgreSQL, cutting checkout p95 latency from 840 ms to 310 ms.",
        why: "The original names no technology and no outcome, so it matches nothing and proves nothing. The rewrite adds four literal keywords a scanner can hit and one number a human can check.",
      },
      {
        before: "Improved test coverage across the codebase.",
        after:
          "Raised unit test coverage on the billing module from 34% to 81% with pytest, and wired the suite into GitHub Actions so a failing test blocks the merge.",
        why: "Coverage without a number is a claim. Naming the tool and the gate turns it into a verifiable practice, and picks up 'unit testing' and 'CI/CD' at the same time.",
      },
      {
        before: "Helped migrate services to Kubernetes.",
        after:
          "Migrated 6 Java services from EC2 to Kubernetes on EKS with Terraform-managed manifests, removing 3 hours of manual release work per week.",
        why: "Helped is unscoreable. The rewrite states the count, the source, the destination, the tooling, and the time it gave back.",
      },
    ],
    mistakes: [
      {
        title: "Listing the framework but not the language",
        body: "A posting that asks for Python will not match a resume that only says Django. Write both. This one habit is usually the single largest coverage gain on an engineering resume.",
      },
      {
        title: "One bullet per project instead of per outcome",
        body: "Bullets are budgeted by tenure. A role under six months earns about three, a multi-year role up to six. Under that ceiling a vague bullet is taking a line a stronger one could have.",
      },
      {
        title: "Writing for peers",
        body: "The hiring manager funds the role and is often not deep in your specialty. A bullet that only another engineer on your exact stack can decode is a bullet that does not survive the first screen.",
      },
    ],
    faqs: [
      {
        q: "How long should a software engineer resume be?",
        a: "One page under about ten years of experience, two pages beyond it. The constraint is not a rule from recruiters; it is that a fixed bullet budget forces every line to win against a stronger one, which is what makes the page dense.",
      },
      {
        q: "Should a software engineer resume include a summary?",
        a: "Only if it says something the bullets cannot. A summary earns no keyword credit from a human reader because it carries no context, so three lines of adjectives cost you a bullet and return nothing. A one-line headline naming your stack and level is the version worth keeping.",
      },
      {
        q: "Do side projects count on a software engineer resume?",
        a: "They count when they carry the same structure as work bullets: the technology, the scope, and a result. A project with users, uptime, or a measurable output competes with a junior work bullet. A tutorial clone does not.",
      },
      {
        q: "Should I tailor my resume for every job application?",
        a: "For every posting you seriously want, yes, because the keyword list changes per posting and the coverage score is computed against that specific list. Tailoring is not rewriting your history; it is reordering emphasis and spelling out the terms that posting uses.",
      },
    ],
    jobsQuery: "software engineer",
  },
  {
    slug: "frontend-developer",
    name: "Frontend Developer",
    updated: "2026-08-29",
    metaTitle: "Frontend Developer Resume: ATS Keywords & Examples",
    metaDescription:
      "A frontend developer resume that passes ATS keyword screens: the literal framework and accessibility terms postings match on, plus before/after bullets that add performance and a11y numbers.",
    definition:
      "A frontend developer resume is screened on framework names, browser platform terms, and increasingly on accessibility and performance vocabulary. React on its own does not match a posting asking for JavaScript, and shipping fast is not the same string as Core Web Vitals or Lighthouse.",
    atsNotes: [
      "Frontend postings list more named tools than almost any other engineering role, which makes literal spelling unusually decisive. The same resume can fail one React posting and pass another purely on whether it wrote Next.js, TypeScript, and Tailwind CSS out in full.",
      "Accessibility terms are the fastest-growing requirement in frontend job descriptions and the one most candidates omit entirely. WCAG, ARIA, keyboard navigation, and screen reader are cheap to add honestly if you have done the work, and they rarely appear on competing resumes.",
      "Design collaboration is a real screening criterion for product-facing frontend roles. A bullet that names Figma handoff, a design system, or component API ownership reads as senior in a way that another framework name does not.",
    ],
    hardKeywords: [
      "JavaScript", "TypeScript", "React", "Next.js", "HTML", "CSS", "Tailwind CSS",
      "REST API", "GraphQL", "Vite", "Webpack", "Jest", "Playwright", "responsive design",
      "WCAG", "ARIA", "Core Web Vitals", "state management", "design system", "Git",
    ],
    softKeywords: [
      "design collaboration", "code review", "component API design",
      "cross-browser testing", "product sense", "mentoring",
    ],
    parentTerms: [
      { child: "React", parent: "JavaScript" },
      { child: "Next.js", parent: "React" },
      { child: "Tailwind CSS", parent: "CSS" },
      { child: "Playwright", parent: "end-to-end testing" },
      { child: "Redux", parent: "state management" },
      { child: "ARIA", parent: "accessibility" },
    ],
    sectionOrder: ["Contact", "Summary", "Experience", "Projects", "Education", "Skills"],
    sectionRationale:
      "Portfolio or live-site URL belongs in the contact line, not at the bottom — it is the fastest evidence you have and a recruiter who clicks it has already spent more than twenty seconds on you. Experience leads; Skills at the end is the home for secondary framework and tooling keywords that did not fit in a bullet with context.",
    bullets: [
      {
        before: "Built the new marketing site in React.",
        after:
          "Rebuilt the marketing site in React and Next.js with server-side rendering, moving Largest Contentful Paint from 4.1 s to 1.3 s and the Lighthouse performance score from 47 to 96.",
        why: "Two framework keywords, one rendering keyword, and two numbers a hiring manager can sanity-check against their own site.",
      },
      {
        before: "Made the app accessible.",
        after:
          "Brought the checkout flow to WCAG 2.2 AA: added ARIA labelling and full keyboard navigation, and cleared 38 axe violations verified with NVDA and VoiceOver.",
        why: "Accessible is not a matchable string. The rewrite names the standard, the level, the tooling, and the screen readers, which most competing resumes will not have.",
      },
      {
        before: "Created reusable components for the team.",
        after:
          "Owned a 42-component design system in TypeScript and Storybook adopted by 4 product teams, cutting new-page build time from about 3 days to 1.",
        why: "Owned plus an adoption count is a scope signal. It reads as one level above 'created components'.",
      },
    ],
    mistakes: [
      {
        title: "Framework name without the language",
        body: "React, Vue, and Svelte are not the string JavaScript or TypeScript. A posting asking for TypeScript screens out a resume that only names the framework.",
      },
      {
        title: "No performance or accessibility numbers",
        body: "Frontend is one of the few specialties where objective metrics are freely available. A resume with no Lighthouse, Core Web Vitals, or bundle-size figure is leaving the easiest credibility on the table.",
      },
      {
        title: "A portfolio link that is not in the header",
        body: "If the link is at the bottom of page two it does not exist. Put it beside your email.",
      },
    ],
    faqs: [
      {
        q: "Does a frontend developer resume need a portfolio?",
        a: "For product and agency roles, effectively yes, and the link belongs in the header. For platform or design-system roles the code matters more than the visuals, and a repository with a readable README serves the same purpose.",
      },
      {
        q: "Should I list every JavaScript framework I have touched?",
        a: "No. List what you can defend in an interview, and put the rest nowhere. A framework you used once becomes a live question you will fail, and a padded Skills list dilutes the terms that actually match the posting.",
      },
      {
        q: "How do I show frontend impact without business metrics?",
        a: "Use platform metrics instead. Load time, bundle size, Lighthouse score, accessibility violations closed, and component adoption across teams are all measurable without access to revenue numbers.",
      },
    ],
    jobsQuery: "frontend developer",
  },
  {
    slug: "backend-developer",
    name: "Backend Developer",
    updated: "2026-08-29",
    metaTitle: "Backend Developer Resume: ATS Keywords & Examples",
    metaDescription:
      "Backend developer resume guide: the database, API, and infrastructure terms ATS screens match literally, plus before/after bullets that add throughput, latency, and reliability numbers.",
    definition:
      "A backend developer resume is screened on language, datastore, API style, and infrastructure keywords. The highest-value fix is almost always spelling out the parent term: a resume full of PostgreSQL, DynamoDB, and Redis still fails a posting that asks for SQL and NoSQL if neither string appears.",
    atsNotes: [
      "Backend postings usually name a language, a database, a cloud, and a messaging or caching layer. Those four categories are where the keyword overlap is won or lost, and a resume missing one whole category rarely clears the screen.",
      "Reliability language separates mid from senior. On-call, incident response, error budget, and post-mortem are terms a scanner matches and a hiring manager weighs, and they are the closest a resume gets to proving system ownership.",
      "Scale numbers are the backend equivalent of a portfolio. Requests per second, rows processed, queue depth, and p95 latency are the figures that make a bullet checkable.",
    ],
    hardKeywords: [
      "Python", "Java", "Go", "Node.js", "SQL", "PostgreSQL", "NoSQL", "Redis",
      "REST API", "gRPC", "GraphQL", "Kafka", "RabbitMQ", "Docker", "Kubernetes",
      "AWS", "CI/CD", "microservices", "database indexing", "caching", "unit testing",
    ],
    softKeywords: [
      "incident response", "on-call", "technical design documents", "code review",
      "cross-team collaboration", "post-mortem",
    ],
    parentTerms: [
      { child: "PostgreSQL", parent: "SQL" },
      { child: "DynamoDB", parent: "NoSQL" },
      { child: "Kafka", parent: "event streaming" },
      { child: "Express", parent: "Node.js" },
      { child: "Spring Boot", parent: "Java" },
      { child: "EKS", parent: "Kubernetes" },
    ],
    sectionOrder: ["Contact", "Summary", "Experience", "Projects", "Education", "Skills"],
    sectionRationale:
      "Backend work is rarely visible, so Experience has to carry everything. Keep Projects only when a project demonstrates a system you have never been paid to build; otherwise cut it and use the space on a stronger Experience bullet. Skills at the end holds the infrastructure and database category keywords the bullets could not carry with full context.",
    bullets: [
      {
        before: "Optimised slow database queries.",
        after:
          "Cut the orders report from 12 s to 400 ms by adding two composite PostgreSQL indexes and replacing an N+1 access pattern, after profiling with EXPLAIN ANALYZE.",
        why: "Names the datastore, the technique, and the diagnostic, and gives a before and an after a reviewer can question.",
      },
      {
        before: "Built a service to handle events.",
        after:
          "Built a Go consumer on Kafka processing 40k events per minute with at-least-once delivery and a dead-letter queue, holding p99 processing under 250 ms.",
        why: "Throughput plus a delivery guarantee is the difference between a toy and a production system.",
      },
      {
        before: "Responsible for uptime of the API.",
        after:
          "Owned on-call for 9 REST services at 99.95% availability; wrote the post-mortems for 4 incidents and closed the top recurring cause with a Redis cache in front of the pricing lookup.",
        why: "Responsible for is a job description, not an achievement. The rewrite gives the count, the number, and what changed because of you.",
      },
    ],
    mistakes: [
      {
        title: "Listing databases but never the words SQL or NoSQL",
        body: "This is the most common single-point failure on backend resumes. Write the specific engine and the category.",
      },
      {
        title: "No scale numbers anywhere",
        body: "Backend work is invisible without them. Requests per second, dataset size, or latency percentiles turn a generic bullet into a checkable one.",
      },
      {
        title: "Treating on-call as too obvious to mention",
        body: "It is a keyword and a seniority signal, and plenty of backend engineers have never carried a pager. Say it.",
      },
    ],
    faqs: [
      {
        q: "How do I write a backend resume with no public product to point at?",
        a: "Point at the system instead of the product. Service count, request volume, data size, latency percentiles, and availability are all statable without naming anything confidential.",
      },
      {
        q: "Should a backend resume include system design work?",
        a: "Yes, and it is underused. A bullet naming a design document you wrote, the alternatives you rejected, and the constraint that decided it reads as a level above implementation work.",
      },
      {
        q: "Is cloud experience mandatory on a backend resume?",
        a: "Nearly. Most backend postings name at least one cloud, and the term is matched literally, so AWS and Azure are not interchangeable strings even though the skills transfer.",
      },
    ],
    jobsQuery: "backend developer",
  },
  {
    slug: "full-stack-developer",
    name: "Full Stack Developer",
    updated: "2026-08-29",
    metaTitle: "Full Stack Developer Resume: ATS Keywords & Examples",
    metaDescription:
      "Full stack developer resume guide: how to cover frontend and backend keywords without diluting either, with before/after bullets that prove end-to-end ownership.",
    definition:
      "A full stack developer resume has to clear two keyword lists on one page, which is why it fails more often on dilution than on omission. The fix is bullets that carry a feature end to end, so a single line earns credit on both the frontend and the backend list.",
    atsNotes: [
      "Screeners for full stack roles usually weight one side harder than the posting admits. Read which stack is named first and which appears in the responsibilities rather than the requirements, and lead with that side.",
      "End-to-end ownership is the differentiator that a specialist resume cannot claim. A bullet that runs from schema to interface proves it in one line and picks up keywords from both categories.",
      "Padding is the specific risk of this role. A Skills list naming eleven technologies you touched once reads as thin, and every one of them is an interview question you can be asked.",
    ],
    hardKeywords: [
      "TypeScript", "JavaScript", "React", "Node.js", "Python", "SQL", "PostgreSQL",
      "REST API", "GraphQL", "Docker", "AWS", "CI/CD", "Git", "authentication",
      "responsive design", "unit testing", "database design", "Agile",
    ],
    softKeywords: [
      "end-to-end ownership", "product collaboration", "code review",
      "requirements gathering", "technical documentation",
    ],
    parentTerms: [
      { child: "Next.js", parent: "React" },
      { child: "Prisma", parent: "SQL" },
      { child: "Express", parent: "Node.js" },
      { child: "OAuth", parent: "authentication" },
      { child: "Vercel", parent: "cloud deployment" },
    ],
    sectionOrder: ["Contact", "Summary", "Experience", "Projects", "Education", "Skills"],
    sectionRationale:
      "Experience leads — end-to-end feature bullets are the proof of claim for this role. Group the Skills block by layer, not alphabetically: frontend, backend, data, infrastructure. A recruiter scanning for one side finds it in a second, and the grouping signals that you think in stacks rather than in tools.",
    bullets: [
      {
        before: "Built features across the stack.",
        after:
          "Shipped subscription billing end to end: PostgreSQL schema, Node.js webhook handlers for Stripe, and the React account UI, taking the feature from spec to production in 3 weeks.",
        why: "One bullet, four keyword categories, and a delivery time that proves the ownership claim instead of asserting it.",
      },
      {
        before: "Worked with the design and product teams.",
        after:
          "Ran weekly scoping with design and product for 3 release cycles, converting Figma flows into a typed REST contract before implementation and removing the rework that had cost about a week per cycle.",
        why: "Collaboration is unscoreable until it produces an artefact. The contract is the artefact.",
      },
      {
        before: "Deployed the application to AWS.",
        after:
          "Containerised the app with Docker and set up a GitHub Actions pipeline to ECS, cutting release time from a 40-minute manual process to a 6-minute push.",
        why: "Names the tools on both sides of the pipeline and states what the automation actually bought.",
      },
    ],
    mistakes: [
      {
        title: "Splitting bullets by layer",
        body: "Separate frontend and backend bullets make you look like two half specialists. Merge them into feature bullets and the ownership becomes the story.",
      },
      {
        title: "A Skills list longer than the Experience section",
        body: "Breadth without depth is the standing objection to full stack candidates. Cut anything you would not defend for ten minutes.",
      },
      {
        title: "Ignoring which side the posting actually weights",
        body: "Most full stack postings are a specialist role with helping duties on the other side. Lead with the side the responsibilities describe.",
      },
    ],
    faqs: [
      {
        q: "Is full stack developer a weaker title than a specialist one?",
        a: "Only when the resume reads as shallow. End-to-end delivery bullets with real numbers beat a specialist resume for product teams; a Skills list of eleven half-known tools loses to one for every team.",
      },
      {
        q: "Which side should a full stack resume lead with?",
        a: "The side the posting spends its responsibilities on. Requirements lists are aspirational; the responsibilities describe the work you would actually do.",
      },
      {
        q: "How many technologies should a full stack Skills section list?",
        a: "As many as you can defend, grouped by layer. In practice that is usually twelve to eighteen terms, not thirty.",
      },
    ],
    jobsQuery: "full stack developer",
  },
  {
    slug: "data-analyst",
    name: "Data Analyst",
    updated: "2026-08-29",
    metaTitle: "Data Analyst Resume: ATS Keywords & Examples",
    metaDescription:
      "Data analyst resume guide: the SQL, BI, and statistics keywords ATS screens match, and before/after bullets that name the decision your analysis changed rather than the chart you built.",
    definition:
      "A data analyst resume is screened on SQL first, a business intelligence tool second, and a statistics vocabulary third. The bullet that separates a strong analyst resume from a weak one names the decision the analysis changed, not the dashboard it produced.",
    atsNotes: [
      "SQL appears in almost every analyst posting and is matched literally. Naming BigQuery or Snowflake without the string SQL is the most common avoidable failure on this resume.",
      "A dashboard is an output, not an outcome. Analyst resumes stall because every bullet ends at delivery; the ones that convert end at the decision, the change, or the money.",
      "Domain vocabulary matters more here than in engineering. Retention, churn, cohort, funnel, attribution, and forecast accuracy are matched terms and they signal that you have worked with a business rather than a dataset.",
    ],
    hardKeywords: [
      "SQL", "Excel", "Python", "pandas", "Tableau", "Power BI", "Looker", "dbt",
      "data visualisation", "A/B testing", "statistical analysis", "regression",
      "cohort analysis", "ETL", "data modelling", "Snowflake", "BigQuery", "KPI reporting",
    ],
    softKeywords: [
      "stakeholder management", "requirements gathering", "presenting to leadership",
      "data storytelling", "cross-functional collaboration",
    ],
    parentTerms: [
      { child: "BigQuery", parent: "SQL" },
      { child: "pandas", parent: "Python" },
      { child: "Tableau", parent: "data visualisation" },
      { child: "dbt", parent: "data modelling" },
      { child: "chi-squared test", parent: "statistical analysis" },
    ],
    sectionOrder: ["Contact", "Summary", "Experience", "Projects", "Education", "Skills"],
    sectionRationale:
      "Experience leads. A fresh quantitative degree is worth highlighting inside the Education entry with a details line naming the relevant coursework — not by moving Education up the page. Projects earn their place when the work history is short or confidential; cut them once two Experience bullets say the same thing better.",
    bullets: [
      {
        before: "Built dashboards in Tableau for the sales team.",
        after:
          "Built a Tableau pipeline-health dashboard on a 40-table SQL model that surfaced a stalled mid-funnel stage; the resulting process change lifted quarter-over-quarter close rate by 6 points.",
        why: "Ends at the decision and its result rather than at the dashboard, and picks up SQL, Tableau, and funnel language on the way.",
      },
      {
        before: "Analysed customer churn.",
        after:
          "Ran a cohort analysis in Python and pandas across 18 months of subscriptions, isolating a 3x churn spike in accounts that never used the import tool and driving an onboarding change that cut 90-day churn from 11% to 7%.",
        why: "Names the method, the window, the finding, and the effect. Every one of those is a keyword and a credibility signal.",
      },
      {
        before: "Automated weekly reporting.",
        after:
          "Replaced a manual weekly Excel report with a dbt model and a scheduled Looker delivery, returning about 6 hours a week to the analytics team and removing three recurring copy-paste errors.",
        why: "Time saved is the analyst metric that is always available, and the error count makes the automation claim concrete.",
      },
    ],
    mistakes: [
      {
        title: "Ending the bullet at the deliverable",
        body: "Built a dashboard, produced a report, ran an analysis. Each one stops one clause short of the thing that gets you hired: what changed.",
      },
      {
        title: "Naming the warehouse but not SQL",
        body: "Snowflake and BigQuery are not the string SQL. Both belong on the page.",
      },
      {
        title: "Listing Excel as an afterthought",
        body: "Excel is still named in a large share of analyst postings and is matched literally. If you use it, write it, including the specific functions if the posting names them.",
      },
    ],
    faqs: [
      {
        q: "Do I need Python on a data analyst resume?",
        a: "Not universally. SQL and a BI tool clear most analyst postings; Python widens the range and is close to required for anything labelled analytics engineer or senior analyst. Add it only if you can actually use pandas under questioning.",
      },
      {
        q: "How do I show impact when I only support other teams?",
        a: "Attribute the decision, not the revenue. 'Analysis that led the team to X, which moved Y' is honest, checkable, and reads stronger than claiming the outcome as yours.",
      },
      {
        q: "Should a data analyst resume include a portfolio?",
        a: "A short one helps when your work history is thin or confidential. Two well-documented analyses with the question, the method, and the conclusion beat ten notebooks.",
      },
    ],
    jobsQuery: "data analyst",
  },
  {
    slug: "data-scientist",
    name: "Data Scientist",
    updated: "2026-08-29",
    metaTitle: "Data Scientist Resume: ATS Keywords & Examples",
    metaDescription:
      "Data scientist resume guide: the modelling, experimentation, and deployment keywords postings match literally, plus bullets that state the offline metric and the shipped effect.",
    definition:
      "A data scientist resume is screened on modelling vocabulary, an experimentation vocabulary, and evidence that a model reached production. The strongest bullets state both numbers: what the model scored offline, and what changed in the business after it shipped.",
    atsNotes: [
      "Postings split sharply into product data science, which is experimentation-heavy, and applied machine learning, which is deployment-heavy. The same resume rarely reads well for both, and the section order should follow whichever you are applying to.",
      "A model that never shipped is a project, not an achievement. Naming the serving path, the monitoring, or the retraining cadence is what separates a research resume from a hiring one.",
      "Offline metrics need to be the right ones. AUC on an imbalanced dataset without precision, recall, or a baseline is a figure a reviewer will discount immediately.",
    ],
    hardKeywords: [
      "Python", "SQL", "pandas", "scikit-learn", "PyTorch", "TensorFlow",
      "machine learning", "statistical modelling", "A/B testing", "experiment design",
      "feature engineering", "regression", "classification", "clustering",
      "time series forecasting", "MLflow", "Spark", "data pipeline", "model deployment",
    ],
    softKeywords: [
      "stakeholder communication", "research design", "cross-functional collaboration",
      "presenting to leadership", "mentoring",
    ],
    parentTerms: [
      { child: "scikit-learn", parent: "machine learning" },
      { child: "PyTorch", parent: "deep learning" },
      { child: "pandas", parent: "Python" },
      { child: "Snowflake", parent: "SQL" },
      { child: "Prophet", parent: "time series forecasting" },
    ],
    sectionOrder: ["Contact", "Summary", "Experience", "Projects", "Education", "Skills", "Publications"],
    sectionRationale:
      "Experience leads. Publications appear only when they are relevant and recent — a stale list reads as an academic resume applying to an industry role. A PhD is worth noting in the Education entry's details line for research roles; it does not need to move up the page for product roles.",
    bullets: [
      {
        before: "Built a churn prediction model.",
        after:
          "Built a gradient-boosted churn model in Python and scikit-learn on 2.1M accounts, reaching 0.79 precision at 0.62 recall against a 0.41 baseline, and served it behind a FastAPI endpoint feeding the retention campaign.",
        why: "Gives the dataset size, both metrics, the baseline, and the serving path. Each of those is a question a reviewer would otherwise have asked.",
      },
      {
        before: "Ran experiments on the onboarding flow.",
        after:
          "Designed and analysed 11 A/B tests on onboarding with pre-registered power calculations; 3 shipped, together lifting week-one activation from 22% to 29%.",
        why: "Reporting how many failed is what makes the ones that shipped believable, and pre-registration signals real experimental discipline.",
      },
      {
        before: "Worked on forecasting for the supply chain team.",
        after:
          "Replaced a spreadsheet demand forecast with a seasonal time series model in Python, cutting mean absolute percentage error from 24% to 9% across 300 SKUs and reducing stockouts in the following quarter.",
        why: "Names the error metric, the before and after, and the scope, which is the only way a forecasting claim can be checked.",
      },
    ],
    mistakes: [
      {
        title: "Accuracy with no baseline",
        body: "94% accuracy means nothing without the class balance and the naive baseline. A reviewer assumes the worst when the baseline is missing.",
      },
      {
        title: "Every project stopping at the notebook",
        body: "If nothing on the page reached a user, the resume reads as coursework. Name one thing that shipped, even a small one.",
      },
      {
        title: "Listing every library ever imported",
        body: "A 30-item tool list dilutes the terms that match and invites questions you cannot answer.",
      },
    ],
    faqs: [
      {
        q: "Do I need a PhD on a data scientist resume?",
        a: "For research roles it is often a hard filter; for product data science it is not, and a shipped model with a measured effect outranks it. Place the degree according to which kind of role you are applying to.",
      },
      {
        q: "Should Kaggle competitions go on a data scientist resume?",
        a: "A high placement in a well-known competition is a real credential and worth one line. A list of participations is filler and costs you a work bullet.",
      },
      {
        q: "How technical should the bullets be?",
        a: "Technical enough to name the method, plain enough that a non-specialist hiring manager understands what changed. If a bullet only makes sense to another data scientist, it will not survive the first screen.",
      },
    ],
    jobsQuery: "data scientist",
  },
  {
    slug: "devops-engineer",
    name: "DevOps Engineer",
    updated: "2026-08-29",
    metaTitle: "DevOps Engineer Resume: ATS Keywords & Examples",
    metaDescription:
      "DevOps engineer resume guide: the cloud, IaC, and observability keywords postings match, plus before/after bullets built on deployment frequency, lead time, and MTTR.",
    definition:
      "A DevOps engineer resume is screened on a named cloud, an infrastructure-as-code tool, a CI/CD system, and an observability stack. The four DORA metrics give this role the clearest numbers of any engineering track, and a resume without at least one of them is competing without evidence.",
    atsNotes: [
      "Clouds are matched literally and are not interchangeable strings. AWS experience does not match an Azure posting even though nearly all of it transfers, so the resume has to name whichever the posting names, honestly.",
      "Deployment frequency, lead time for changes, change failure rate, and mean time to restore are the metrics this role is measured on in practice. Any one of them in a bullet immediately outperforms a tool list.",
      "Cost is the second axis and is badly underused. Cloud spend reduced, instances retired, and reserved-capacity savings are checkable numbers that few competing resumes carry.",
    ],
    hardKeywords: [
      "AWS", "Azure", "GCP", "Terraform", "Kubernetes", "Docker", "CI/CD",
      "GitHub Actions", "Jenkins", "Ansible", "Linux", "Bash", "Python",
      "Prometheus", "Grafana", "observability", "infrastructure as code",
      "incident response", "SRE", "cost optimisation",
    ],
    softKeywords: [
      "on-call", "post-mortem", "runbook authoring", "developer enablement",
      "cross-team collaboration",
    ],
    parentTerms: [
      { child: "Terraform", parent: "infrastructure as code" },
      { child: "EKS", parent: "Kubernetes" },
      { child: "GitHub Actions", parent: "CI/CD" },
      { child: "Prometheus", parent: "observability" },
      { child: "Bash", parent: "scripting" },
    ],
    sectionOrder: ["Contact", "Summary", "Experience", "Projects", "Education", "Skills", "Certifications"],
    sectionRationale:
      "Experience leads because infrastructure work is proved in production, not on paper. Certifications appear after Skills because cloud certs are a genuine screening filter here — a recruiter will scan for them. Keep only current ones; an expired certification reads worse than none.",
    bullets: [
      {
        before: "Managed AWS infrastructure with Terraform.",
        after:
          "Codified 140 AWS resources across 3 accounts in Terraform with remote state and plan-on-PR review, ending manual console changes and cutting environment provisioning from 2 days to 20 minutes.",
        why: "Resource count and account count are scope. The provisioning time is the payoff, and the review gate shows how you work.",
      },
      {
        before: "Improved the CI/CD pipeline.",
        after:
          "Rebuilt the CI pipeline in GitHub Actions with layer caching and parallel test shards, taking the build from 27 minutes to 6 and lifting deployment frequency from weekly to 9 times a day.",
        why: "Two DORA metrics in one line, plus the specific techniques that produced them.",
      },
      {
        before: "Handled monitoring and alerting.",
        after:
          "Instrumented 22 services with Prometheus and Grafana and rewrote the alert rules around symptom-based SLOs, cutting pager volume by 64% and mean time to restore from 51 to 18 minutes.",
        why: "Alert-noise reduction is the credibility signal here, because anyone who has carried a pager knows how hard it is.",
      },
    ],
    mistakes: [
      {
        title: "A tool list with no metrics",
        body: "This role has the best-defined metrics in engineering. A resume that lists twenty tools and no numbers looks like someone who watched the pipeline rather than owned it.",
      },
      {
        title: "Claiming a cloud you have only read about",
        body: "Multi-cloud claims are checked hard in interviews. Name the one you have run in production and say the others are adjacent.",
      },
      {
        title: "Leaving out cost",
        body: "Cloud spend is a board-level line item. A bullet that saved real money is memorable and rarely present on competing resumes.",
      },
    ],
    faqs: [
      {
        q: "Are cloud certifications worth putting on a DevOps resume?",
        a: "Yes, more than in most engineering roles: they are used as a filter by recruiters who cannot assess the work directly. List only current ones with the year.",
      },
      {
        q: "What is the difference between a DevOps and an SRE resume?",
        a: "DevOps postings weight delivery pipelines, infrastructure as code, and developer enablement. SRE postings weight SLOs, error budgets, incident command, and reliability engineering. The keyword lists overlap by roughly half, so the emphasis has to be chosen per posting.",
      },
      {
        q: "How do I show DevOps impact from a small team?",
        a: "Small teams produce the clearest before-and-after numbers, because you are usually replacing something manual. Time returned per week, releases per day, and hours of downtime avoided all work without a large organisation behind them.",
      },
    ],
    jobsQuery: "devops engineer",
  },
  {
    slug: "product-manager",
    name: "Product Manager",
    updated: "2026-08-29",
    metaTitle: "Product Manager Resume: ATS Keywords & Examples",
    metaDescription:
      "Product manager resume guide: the discovery, delivery, and analytics keywords postings match, plus bullets that state the decision you made and the metric it moved.",
    definition:
      "A product manager resume is judged on decisions, not on features shipped. The screening vocabulary covers discovery, prioritisation, and analytics, and the bullets that convert name the thing you chose not to build as often as the thing you did.",
    atsNotes: [
      "Product postings match on method names more than tools: user research, discovery, prioritisation, roadmap, and experimentation are the terms that recur, and the analytics tools are secondary.",
      "The credibility problem specific to this role is ownership. A team ships a feature; a product manager decides. Bullets phrased as 'launched X' read as project management, while 'chose X over Y because Z, which moved M' reads as product.",
      "Metrics have to be the ones you can influence. Activation, retention, conversion, and time to value are defensible; company revenue usually is not, and claiming it invites a question you will lose.",
    ],
    hardKeywords: [
      "product roadmap", "user research", "discovery", "prioritisation",
      "A/B testing", "SQL", "product analytics", "Amplitude", "Mixpanel",
      "requirements", "PRD", "go-to-market", "stakeholder management",
      "Agile", "Scrum", "Jira", "Figma", "KPI", "OKR", "customer interviews",
    ],
    softKeywords: [
      "cross-functional leadership", "executive communication", "negotiation",
      "influence without authority", "mentoring",
    ],
    parentTerms: [
      { child: "Amplitude", parent: "product analytics" },
      { child: "Jira", parent: "Agile" },
      { child: "PRD", parent: "requirements" },
      { child: "customer interviews", parent: "user research" },
    ],
    sectionOrder: ["Contact", "Summary", "Experience", "Education", "Skills"],
    sectionRationale:
      "Product is one of the few roles with no Projects section — personal side projects rarely prove PM judgement. Experience leads; Skills is at the end because a tool list at the top of a PM resume leads with the wrong thing. The judgement is the qualification.",
    bullets: [
      {
        before: "Launched a new onboarding flow.",
        after:
          "Cut onboarding from 9 steps to 4 after 22 customer interviews identified the import step as the drop-off, lifting week-one activation from 31% to 44% across 12,000 monthly signups.",
        why: "Names the research volume, the specific finding, the decision, and the metric. It is a decision bullet rather than a launch bullet.",
      },
      {
        before: "Managed the product roadmap for the platform team.",
        after:
          "Owned a 3-quarter platform roadmap for 2 engineering teams; cut 4 committed features after a usage review showed under 2% adoption, redirecting the quarter to an API that 3 enterprise accounts had blocked renewal on.",
        why: "Killing work is the hardest thing to prove and the strongest product signal on a resume.",
      },
      {
        before: "Worked with engineering and design daily.",
        after:
          "Ran discovery and delivery for a 7-person squad on a 2-week cycle, writing the PRDs and the acceptance criteria, and held average cycle time under 6 days across 3 quarters.",
        why: "Team size and cadence are scope, and cycle time is a delivery metric a PM can honestly own.",
      },
    ],
    mistakes: [
      {
        title: "Claiming company revenue",
        body: "A single PM rarely owns a revenue line. Claiming it reads as inflation and the interview will test it. Own the metric closest to your surface.",
      },
      {
        title: "Feature lists instead of decisions",
        body: "Shipped A, B, and C describes a backlog. What you chose, why, and what it moved describes a product manager.",
      },
      {
        title: "No numbers because the product was pre-launch",
        body: "Pre-launch work still has numbers: interviews run, prototypes tested, concepts killed, time to first customer. Use those rather than none.",
      },
    ],
    faqs: [
      {
        q: "Does a product manager resume need SQL?",
        a: "It is named in a growing share of postings and is matched literally, so include it if you genuinely write queries. If you only read dashboards, write product analytics instead and do not claim SQL.",
      },
      {
        q: "How technical should a PM resume be for a technical product?",
        a: "Technical enough to show you can hold a design discussion: name the system, the constraint, and the trade-off. Do not write engineering bullets; you will be measured against engineers and lose.",
      },
      {
        q: "Should a PM resume be one page?",
        a: "One page under about eight years. PM resumes bloat faster than engineering ones because every project has a story, and the discipline of cutting is itself the signal being assessed.",
      },
    ],
    jobsQuery: "product manager",
  },
  {
    slug: "qa-engineer",
    name: "QA Engineer",
    updated: "2026-08-29",
    metaTitle: "QA Engineer Resume: ATS Keywords & Examples",
    metaDescription:
      "QA engineer resume guide: the automation, framework, and process keywords postings match, plus bullets built on escaped defects, suite runtime, and coverage rather than tickets closed.",
    definition:
      "A QA engineer resume is screened on automation framework names first and process vocabulary second. The metric that separates a strong one is escaped defects, meaning bugs that reached production, because it is the only QA number that measures the outcome rather than the activity.",
    atsNotes: [
      "Manual and automation QA are now largely separate keyword lists. A resume aiming at automation roles needs a language, a framework, and a CI system named explicitly, or it does not clear the screen at all.",
      "Test counts are weak evidence. Wrote 400 test cases says nothing about whether the right things were tested; suite runtime, flake rate, and escaped defects say a great deal.",
      "Shift-left vocabulary is increasingly requested: contributing to acceptance criteria, reviewing designs for testability, and building test data. It is matched literally and signals a QA engineer embedded in the team rather than downstream of it.",
    ],
    hardKeywords: [
      "test automation", "Selenium", "Playwright", "Cypress", "Python", "JavaScript",
      "pytest", "API testing", "Postman", "CI/CD", "regression testing",
      "test plan", "test case design", "performance testing", "JMeter",
      "SQL", "Jira", "Agile", "exploratory testing", "accessibility testing",
    ],
    softKeywords: [
      "risk-based testing", "requirements review", "cross-team collaboration",
      "release sign-off", "defect triage",
    ],
    parentTerms: [
      { child: "Playwright", parent: "test automation" },
      { child: "pytest", parent: "Python" },
      { child: "Postman", parent: "API testing" },
      { child: "JMeter", parent: "performance testing" },
    ],
    sectionOrder: ["Contact", "Summary", "Experience", "Projects", "Education", "Skills"],
    sectionRationale:
      "Experience leads. QA postings are unusually tool-specific, so the Skills block at the end is the first place a recruiter jumps to after skimming Experience — make it dense and correctly spelled. A framework name spelled wrong (Playwright vs playwright) is an instant miss.",
    bullets: [
      {
        before: "Wrote automated tests for the web application.",
        after:
          "Built a Playwright suite of 260 end-to-end tests in TypeScript covering the 12 highest-revenue flows, running on every pull request in under 8 minutes.",
        why: "Coverage aimed at revenue flows shows risk-based thinking, and the runtime shows the suite is actually usable in CI.",
      },
      {
        before: "Reduced the number of bugs in production.",
        after:
          "Cut escaped defects from 14 to 3 per release over 4 releases by adding contract tests at the API boundary and moving smoke checks into the deployment pipeline.",
        why: "Escaped defects is the outcome metric for QA, and the bullet names the two changes that produced it.",
      },
      {
        before: "Maintained the regression test suite.",
        after:
          "Cut suite flake rate from 19% to 2% by isolating test data per run and replacing 40 sleep-based waits with explicit conditions, restoring team trust in the red build.",
        why: "Flake is the reason automation gets ignored. Fixing it is a senior QA achievement and is rarely on competing resumes.",
      },
    ],
    mistakes: [
      {
        title: "Counting test cases",
        body: "Volume is not quality. A suite of 400 tests that nobody trusts is worse than 80 that gate a release.",
      },
      {
        title: "No language named next to the framework",
        body: "Selenium is not the string Java or Python. Automation postings screen on both.",
      },
      {
        title: "Framing QA as downstream",
        body: "Tested what the team built reads junior. Reviewed acceptance criteria, argued for testability, and caught it before it was built reads senior.",
      },
    ],
    faqs: [
      {
        q: "Is manual QA still hireable?",
        a: "Yes, mainly in regulated, hardware-adjacent, and complex-domain products, but the postings are fewer and pay less than automation ones. A resume that names one automation framework honestly opens a substantially larger pool.",
      },
      {
        q: "What is the strongest metric on a QA resume?",
        a: "Escaped defects per release, because it measures the outcome rather than the activity. Suite runtime and flake rate are the next two.",
      },
      {
        q: "Should a QA engineer resume mention performance and security testing?",
        a: "Only if you have done them. Both are matched keywords and both are heavily probed in interviews, so an unearned mention costs more than it gains.",
      },
    ],
    jobsQuery: "qa engineer",
  },
  {
    slug: "machine-learning-engineer",
    name: "Machine Learning Engineer",
    updated: "2026-08-29",
    metaTitle: "Machine Learning Engineer Resume: ATS Keywords & Examples",
    metaDescription:
      "Machine learning engineer resume guide: the modelling, serving, and MLOps keywords postings match, plus bullets that prove a model reached production and stayed there.",
    definition:
      "A machine learning engineer resume is screened as an engineering resume with modelling on top. Postings match on a framework, a serving path, and an MLOps tool, and the bullets that convert prove a model reached production, was monitored, and was retrained.",
    atsNotes: [
      "The line against data scientists is production. Training code, an endpoint, latency under load, and a retraining schedule are the things that make this an ML engineering resume rather than a research one.",
      "Inference cost and latency have become standard requirements as models moved into real products. Quantisation, batching, caching, and GPU utilisation are matched terms and most competing resumes lack them.",
      "For roles touching large language models, name the concrete pieces: retrieval-augmented generation, evaluation harness, prompt versioning, and vector database. Vague AI experience matches nothing.",
    ],
    hardKeywords: [
      "Python", "PyTorch", "TensorFlow", "scikit-learn", "machine learning",
      "deep learning", "model deployment", "MLOps", "MLflow", "Docker",
      "Kubernetes", "AWS SageMaker", "feature store", "data pipeline", "Spark",
      "model monitoring", "A/B testing", "inference latency", "vector database", "RAG",
    ],
    softKeywords: [
      "cross-functional collaboration", "design documents", "on-call",
      "mentoring", "research to production handoff",
    ],
    parentTerms: [
      { child: "PyTorch", parent: "deep learning" },
      { child: "SageMaker", parent: "AWS" },
      { child: "MLflow", parent: "MLOps" },
      { child: "pgvector", parent: "vector database" },
      { child: "RAG", parent: "retrieval-augmented generation" },
    ],
    sectionOrder: ["Contact", "Summary", "Experience", "Projects", "Education", "Skills"],
    sectionRationale:
      "Experience leads. Projects earn their place here longer than in most engineering roles — a deployed personal model with real traffic is scarce evidence of production ML. Cut a project once two Experience bullets say the same thing better.",
    bullets: [
      {
        before: "Deployed machine learning models to production.",
        after:
          "Deployed a PyTorch ranking model behind a Kubernetes inference service at 900 requests per second and p95 latency of 40 ms, with drift monitoring and a weekly retraining job in MLflow.",
        why: "Throughput, latency, monitoring, and retraining are the four things that prove a model actually lives in production.",
      },
      {
        before: "Reduced model inference cost.",
        after:
          "Cut GPU inference cost 62% by quantising the model to int8 and batching requests, holding offline accuracy within 0.4 points of the full-precision baseline.",
        why: "Stating what the optimisation cost in accuracy is what makes the saving credible.",
      },
      {
        before: "Worked on an internal LLM feature.",
        after:
          "Built a retrieval-augmented answering service over 120k support documents using pgvector and a reranking step, and an evaluation harness of 300 graded questions that raised answer accuracy from 61% to 84%.",
        why: "The evaluation harness is the part that separates engineering from prompt tinkering, and it names four matched keywords.",
      },
    ],
    mistakes: [
      {
        title: "A resume of notebooks",
        body: "If nothing served traffic, the resume reads as data science applying to engineering. One production path fixes it.",
      },
      {
        title: "Model metrics without system metrics",
        body: "Accuracy with no latency, throughput, or cost tells a hiring manager you have not run the thing under load.",
      },
      {
        title: "Vague AI experience",
        body: "Worked with LLMs matches nothing and proves nothing. Name the retrieval strategy, the evaluation method, and the store.",
      },
    ],
    faqs: [
      {
        q: "What is the difference between a machine learning engineer and a data scientist resume?",
        a: "Roughly half the keyword list is shared, but the ML engineering resume has to show serving, monitoring, and retraining. A data science resume can stop at the analysis and the experiment.",
      },
      {
        q: "Do I need Kubernetes on an ML engineer resume?",
        a: "It appears in a large share of postings and is matched literally, but a managed serving platform such as SageMaker or Vertex AI is an acceptable substitute if that is what you have actually used.",
      },
      {
        q: "How do I show LLM work that has no accuracy metric?",
        a: "Build and describe the evaluation you used, even a small graded set. An honest evaluation harness of 200 questions is stronger evidence than an unmeasured production feature.",
      },
    ],
    jobsQuery: "machine learning engineer",
  },
  {
    slug: "mobile-developer",
    name: "Mobile Developer",
    updated: "2026-08-29",
    metaTitle: "Mobile Developer Resume: ATS Keywords & Examples",
    metaDescription:
      "Mobile developer resume guide for iOS, Android, and React Native: the platform keywords postings match, plus bullets built on crash-free rate, app size, and store metrics.",
    definition:
      "A mobile developer resume is screened per platform, and the platform strings are not interchangeable. Swift does not match a Kotlin posting, and React Native matches neither unless written out. The metrics that carry weight are crash-free sessions, cold start time, app size, and store rating.",
    atsNotes: [
      "Decide the platform before tailoring. A resume that hedges across iOS, Android, and cross-platform reads as shallow on all three, and the keyword dilution costs coverage on each list.",
      "Shipped apps are the strongest evidence in this specialty. Store links, download counts, and rating are checkable in seconds, which is exactly what makes them persuasive.",
      "Release engineering is underrated. App Store review, phased rollout, feature flags, and crash triage are matched terms and they signal someone who has owned a release rather than only written screens.",
    ],
    hardKeywords: [
      "Swift", "SwiftUI", "Kotlin", "Jetpack Compose", "iOS", "Android",
      "React Native", "Flutter", "REST API", "unit testing", "UI testing",
      "App Store", "Google Play", "CI/CD", "Fastlane", "push notifications",
      "offline support", "accessibility", "crash reporting", "performance profiling",
    ],
    softKeywords: [
      "design collaboration", "release ownership", "code review",
      "cross-functional collaboration", "user feedback triage",
    ],
    parentTerms: [
      { child: "SwiftUI", parent: "iOS" },
      { child: "Jetpack Compose", parent: "Android" },
      { child: "React Native", parent: "JavaScript" },
      { child: "Fastlane", parent: "CI/CD" },
      { child: "Firebase Crashlytics", parent: "crash reporting" },
    ],
    sectionOrder: ["Contact", "Summary", "Experience", "Shipped Apps", "Education", "Skills"],
    sectionRationale:
      "Experience leads. A Shipped Apps block with store links replaces Projects here — a reviewer can verify a live app instantly. Keep it to apps that are live and that you would be happy to have opened in front of you. Skills at the end holds the platform and SDK keywords the bullets could not carry in full context.",
    bullets: [
      {
        before: "Developed features for the iOS app.",
        after:
          "Shipped 14 features to a 300k-monthly-user iOS app in Swift and SwiftUI, holding crash-free sessions above 99.7% across 9 releases.",
        why: "User scale plus crash-free rate is the mobile equivalent of uptime, and both are checkable.",
      },
      {
        before: "Improved app performance.",
        after:
          "Cut Android cold start from 3.4 s to 1.1 s by deferring third-party SDK initialisation and trimming the startup dependency graph, verified with Macrobenchmark.",
        why: "Cold start is the mobile metric users feel first, and naming the measurement tool makes the number defensible.",
      },
      {
        before: "Handled app releases.",
        after:
          "Automated the release with Fastlane and phased rollout, taking a 2-day manual submission process to a 40-minute pipeline and catching 2 regressions at 5% rollout before general release.",
        why: "Release ownership plus a caught regression proves the process worked rather than merely existed.",
      },
    ],
    mistakes: [
      {
        title: "Hedging across three platforms",
        body: "Pick the one the posting names and lead with it. The others belong in one line in Skills, not spread across every bullet.",
      },
      {
        title: "No shipped app anywhere on the page",
        body: "Mobile is a specialty where the artefact is public. If everything you built was internal, say so and give the user count instead.",
      },
      {
        title: "Screens instead of outcomes",
        body: "Built the settings screen is a task. What it changed for users, crashes, or release speed is an achievement.",
      },
    ],
    faqs: [
      {
        q: "Does React Native experience count for a native iOS role?",
        a: "Partly, and not as a keyword. The posting matches Swift; React Native does not satisfy it. If you have both, name both, and lead with whichever the posting asks for.",
      },
      {
        q: "Should I list apps that are no longer in the store?",
        a: "Yes, with the user numbers and the dates, but do not link a dead page. An unlinked claim with a number reads better than a broken link.",
      },
      {
        q: "How important is accessibility on a mobile resume?",
        a: "Increasingly a named requirement, especially for consumer and public-sector apps. Dynamic Type, VoiceOver, and TalkBack are matched terms and few competing resumes have them.",
      },
    ],
    jobsQuery: "mobile developer",
  },
  {
    slug: "ui-ux-designer",
    name: "UI/UX Designer",
    updated: "2026-08-29",
    metaTitle: "UI/UX Designer Resume: ATS Keywords & Examples",
    metaDescription:
      "UI/UX designer resume guide: the research, systems, and accessibility keywords postings match, plus bullets that state the usability problem found and the metric the redesign moved.",
    definition:
      "A UI/UX designer resume is read alongside a portfolio, which means its job is to state outcomes the portfolio cannot: what you researched, what you decided, and what the metric did afterwards. Screening vocabulary covers research methods, design systems, and accessibility standards.",
    atsNotes: [
      "The portfolio decides the interview, but the resume decides whether the portfolio is opened. That makes the header link and the first two bullets the highest-value real estate on the page.",
      "Research method names are matched literally and are the clearest seniority signal: usability testing, contextual enquiry, card sorting, and diary study each imply a different depth of practice.",
      "Design system work is the strongest single differentiator on a designer resume, because it demonstrates influence beyond one screen and is easy to quantify by component count and adopting teams.",
    ],
    hardKeywords: [
      "Figma", "design system", "prototyping", "wireframing", "user research",
      "usability testing", "information architecture", "interaction design",
      "accessibility", "WCAG", "responsive design", "design tokens",
      "user flows", "A/B testing", "personas", "journey mapping", "design critique",
    ],
    softKeywords: [
      "stakeholder alignment", "cross-functional collaboration", "facilitation",
      "presenting to leadership", "mentoring",
    ],
    parentTerms: [
      { child: "Figma", parent: "prototyping" },
      { child: "WCAG", parent: "accessibility" },
      { child: "card sorting", parent: "information architecture" },
      { child: "design tokens", parent: "design system" },
    ],
    sectionOrder: ["Contact", "Summary", "Experience", "Education", "Skills"],
    sectionRationale:
      "Portfolio URL goes in the contact line, never at the bottom — it is the actual work. Experience leads; Skills at the end is the least differentiating thing on a designer resume, because every applicant has Figma. Research methods and accessibility standards in Skills are what actually separate applicants.",
    bullets: [
      {
        before: "Redesigned the checkout experience.",
        after:
          "Redesigned checkout after 12 moderated usability sessions showed users abandoning at the address step, cutting steps from 6 to 3 and lifting completion from 58% to 71%.",
        why: "Research volume, the specific finding, the change, and the metric. It shows a designer who investigated rather than restyled.",
      },
      {
        before: "Created a design system for the product.",
        after:
          "Built and documented a 60-component design system in Figma with design tokens shared with engineering, adopted by 4 product teams and cutting new-screen design time by about half.",
        why: "Component count and adoption across teams is scope, and the token handoff shows the system reached code.",
      },
      {
        before: "Made designs accessible.",
        after:
          "Brought the core product to WCAG 2.2 AA in design: fixed 27 contrast failures, specified focus order for every flow, and added screen reader annotations to the handoff files.",
        why: "Accessibility is a matched keyword and a growing requirement, and the specifics prove it went past a colour check.",
      },
    ],
    mistakes: [
      {
        title: "A resume that describes the portfolio",
        body: "The two documents have different jobs. The resume states outcomes and scope; the portfolio shows the work. Repeating the case study wastes the page.",
      },
      {
        title: "Tool lists as the differentiator",
        body: "Everyone has Figma. Research methods, systems work, and accessibility standards are what separate applicants.",
      },
      {
        title: "No metrics because design is qualitative",
        body: "Task completion, time on task, error rate, support tickets, and adoption are all design metrics and all obtainable without owning revenue.",
      },
    ],
    faqs: [
      {
        q: "Should a UI/UX resume be visually designed?",
        a: "Restrained, yes; decorated, no. Multi-column layouts, text in images, and icon-only headings break parsing, so the safest strong choice is a single-column layout with excellent typography.",
      },
      {
        q: "Do UX designers need to know how to code?",
        a: "Not to be hired, but naming design tokens, component APIs, or a handoff process you own is a real differentiator because it proves the system survived contact with engineering.",
      },
      {
        q: "How many case studies should the portfolio hold?",
        a: "Three deep ones beat eight shallow ones. Each should state the problem, what you did, what you rejected, and what happened, which is exactly the structure the resume bullets mirror.",
      },
    ],
    jobsQuery: "ui ux designer",
  },
];

export const ROLE_GUIDE_BY_SLUG = new Map(ROLE_GUIDES.map((g) => [g.slug, g]));
